import React, { useContext, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { applyFieldUpdates, getNestedValue } from '../../utils/pageContentDefaults';
import Modal from '../ui/Modal';
import ListItemFields from './ListItemFields';
import '../../styles/components/admin-content-edit.css';

const normalizeLinesDraft = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value : [''];
  }
  const lines = String(value || '').split('\n');
  return lines.length > 0 ? lines : [''];
};

const isThemePreviewEmbed = () =>
  typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('themePreview') === '1';

const AdminEditableSection = ({ title, fields, children, className = '' }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const { pageContent, updatePageContent } = useSettings();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isAdmin = isAuthenticated && user?.role === 'admin';
  const wrapperClassName = ['admin-editable-section', className].filter(Boolean).join(' ');

  const openEditor = () => {
    const initialDraft = {};
    fields.forEach((field) => {
      const value = getNestedValue(pageContent, field.path);
      initialDraft[field.name] = field.type === 'lines'
        ? normalizeLinesDraft(value)
        : (value ?? '');
    });
    setDraft(initialDraft);
    setError(null);
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const nextContent = applyFieldUpdates(pageContent, fields, draft);
      await updatePageContent(nextContent);
      setOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin || isThemePreviewEmbed()) {
    if (!className) {
      return children;
    }
    return <div className={className}>{children}</div>;
  }

  return (
    <>
      <div className={wrapperClassName}>
        <button
          type="button"
          className="admin-editable-trigger"
          onClick={openEditor}
          aria-label={`Edit ${title}`}
        >
          <FaEdit /> Edit
        </button>
        {children}
      </div>

      {open && (
        <Modal
          title={title}
          onClose={() => !saving && setOpen(false)}
          onConfirm={handleSave}
          confirmText={saving ? 'Saving...' : 'Save'}
          confirmType="primary"
        >
          <div className="admin-editable-form">
            {fields.map((field, index) => {
              const showSection = field.section && (index === 0 || fields[index - 1].section !== field.section);

              return (
                <React.Fragment key={field.name}>
                  {showSection && (
                    <div className="admin-editable-section-heading">{field.section}</div>
                  )}
                  <div className={`admin-editable-field ${field.type === 'lines' ? 'admin-editable-field-list' : ''}`}>
                    <label htmlFor={field.name}>{field.label}</label>
                    {field.type === 'lines' ? (
                      <ListItemFields
                        items={draft[field.name] || ['']}
                        itemLabel="Item"
                        addLabel="Add item"
                        onChange={(itemIndex, value) => {
                          setDraft((prev) => {
                            const nextItems = [...(prev[field.name] || [''])];
                            nextItems[itemIndex] = value;
                            return { ...prev, [field.name]: nextItems };
                          });
                        }}
                        onAdd={() => {
                          setDraft((prev) => ({
                            ...prev,
                            [field.name]: [...(prev[field.name] || ['']), '']
                          }));
                        }}
                        onRemove={(itemIndex) => {
                          setDraft((prev) => ({
                            ...prev,
                            [field.name]: (prev[field.name] || ['']).filter((_, i) => i !== itemIndex)
                          }));
                        }}
                      />
                    ) : field.type === 'textarea' ? (
                      <textarea
                        id={field.name}
                        rows={4}
                        value={draft[field.name] ?? ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={field.placeholder}
                      />
                    ) : (
                      <input
                        id={field.name}
                        type="text"
                        value={draft[field.name] ?? ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, [field.name]: e.target.value }))}
                        placeholder={field.placeholder}
                      />
                    )}
                    {field.help && <p className="admin-editable-help">{field.help}</p>}
                  </div>
                </React.Fragment>
              );
            })}
            {error && <p className="admin-editable-error">{error}</p>}
          </div>
        </Modal>
      )}
    </>
  );
};

export default AdminEditableSection;
